import React from "react";
import PropTypes from "prop-types";
import Infinite from "react-infinite";
import selectn from "selectn";

import Spinner from "components/Library/Spinner";
import LoadingStoriesNotice from "components/Library/LoadingStoriesNotice";
import StreamItem from "stream/components/StreamItem";
import Log from "utils/log";

import { STREAM_TYPE } from "config/constants";

import styles from "./PaginatedStream.css";

/**
 * PaginatedStream
 * This is the main stream (content feed) component on Axios.com
 * It takes content in via a prop called "Edges" (which is a graphQl convention)
 * and iterates over the array to return an array of "StreamItem" components.
 * This component also uses the now abandoned "React-Infinite" library to handle
 * the virtualization of the stream.
 */
export default class PaginatedStream extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      hashedEdgeIds: null,
      elementHeights: {},
      attemptInfiniteLoad: props.edges.length > 1 ? true : undefined, // Has to be undefined because of react-infinite
      isLoading: true, // Make sure this is correct for server rendered streams with fewer than ten edges
    };
  }

  static getDerivedStateFromProps(props, state) {
    const newEdgesHash = PaginatedStream.hashEdgeIds(props.edges);
    const hasNewEdges = newEdgesHash !== state.hashedEdgeIds;
    const loadingStateChanged = props.isLoading !== state.isLoading;

    const newState = {};

    if (loadingStateChanged) {
      newState.isLoading = props.isLoading;
    }

    if (!props.isLoading && hasNewEdges) {
      newState.hashedEdgeIds = newEdgesHash;

      // If the stream has a next page and the component is mounted on the
      // client, we can attempt an infinite load. This prevents accidentally
      // loading a second page before the non-server rendered nodes are rendered.
      // Has to be undefined, not false: https://github.com/seatgeek/react-infinite/blob/master/src/react-infinite.jsx#L450
      newState.attemptInfiniteLoad =
        props.pageInfo.hasNextPage && props.isClient ? true : undefined;

      newState.elementHeights = PaginatedStream.getElementHeightsForNextEdges(
        props.edges,
        state.elementHeights,
        props.defaultCardHeight
      );
    } else if (!props.isLoading && loadingStateChanged && !hasNewEdges) {
      // This will be replaced by https://axioscode.atlassian.net/browse/SITE-571
      newState.attemptInfiniteLoad = undefined; // Handle unraised Apollo error
    }

    return newState;
  }

  static getElementHeightsForNextEdges(
    nextEdges,
    prevElementHeights,
    defaultCardHeight
  ) {
    return nextEdges.reduce((accum, edge) => {
      return {
        ...accum,
        [edge.node.id]:
          selectn(`${edge.node.id}`, prevElementHeights) || defaultCardHeight,
      };
    }, {});
  }

  static hashEdgeIds(edges) {
    return edges.map(edge => edge.node.id).join("");
  }

  render() {
    if (
      Object.keys(this.props.edges).length !==
      Object.keys(this.state.elementHeights).length
    ) {
      Log.logger("edges length does not match elementHeights length", "error", {
        edges: this.props.edges,
        elementHeights: this.state.elementHeights,
      });
    }

    // const elementHeights = Object.values(this.state.elementHeights);
    const elementHeights = this.props.edges.map(
      edge =>
        this.state.elementHeights[edge.node.id] || this.props.defaultCardHeight
    );
    const containerProps = this.props.isClient
      ? { useWindowAsScrollContainer: true }
      : { containerHeight: 2000 };

    const isLoadingStories = this.state.isLoading || !this.props.isClient;

    return (
      <>
        <Infinite
          className={styles.root}
          elementHeight={elementHeights}
          infiniteLoadBeginEdgeOffset={
            this.state.attemptInfiniteLoad && this.props.defaultCardHeight
          }
          preloadBatchSize={Infinite.containerHeightScaleFactor(2)}
          preloadAdditionalHeight={Infinite.containerHeightScaleFactor(30)}
          loadingSpinnerDelegate={<Spinner />}
          onInfiniteLoad={this.props.fetchNextPage}
          isInfiniteLoading={isLoadingStories}
          {...containerProps}
        >
          {this.props.edges.map((edge, index) => {
            return (
              <StreamItem
                edge={edge}
                index={index}
                key={edge.node.id}
                contentComponent={this.props.contentComponent}
                streamType={this.props.stream.type}
                registerStreamItemNode={this.props.registerStreamItemNode}
                unregisterStreamItemNode={this.props.unregisterStreamItemNode}
                showSaveTag={this.props.showSaveTag}
              />
            );
          })}
        </Infinite>
        <LoadingStoriesNotice isLoading={isLoadingStories} />
      </>
    );
  }
}

PaginatedStream.propTypes = {
  fetchNextPage: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  pageInfo: PropTypes.shape({
    hasNextPage: PropTypes.bool.isRequired,
  }).isRequired,
  edges: PropTypes.arrayOf(
    PropTypes.shape({
      node: PropTypes.object.isRequired,
    })
  ).isRequired,
  defaultCardHeight: PropTypes.number.isRequired,
  contentComponent: PropTypes.func.isRequired,
  stream: PropTypes.shape({
    type: PropTypes.oneOf(Object.values(STREAM_TYPE)).isRequired,
  }).isRequired,
  registerStreamItemNode: PropTypes.func,
  unregisterStreamItemNode: PropTypes.func,
  isClient: PropTypes.bool,
  showSaveTag: PropTypes.bool,
};
